/**
 * Bottom sheet for a list row's full detail, on phones.
 *
 * Every list on this dashboard drops columns as the viewport narrows —
 * that is what keeps the rows readable. The dropped values still have to
 * be reachable, so a tap on the row opens them here rather than forcing
 * the table to scroll sideways for them.
 *
 * It is deliberately dumb: the caller passes a title, an optional
 * subtitle, a list of label/value pairs and up to two actions. Nothing
 * about batches or students is known here, so every list can use it.
 *
 * Desktop keeps whatever it already did — the modal, or the SweetAlert
 * panel. `useIsMobile` is what decides; it now lives in
 * utils/useIsMobile and is re-exported below for callers that still
 * take it from here.
 */

import { useEffect } from 'react';

/* Moved to utils/useIsMobile: the student dashboard and the header's
   notification bell read it too, and neither should be importing from
   a trainer folder to get it. Re-exported here so the callers that
   already take it from this module keep working. */
export { useIsMobile } from '../../../../utils/useIsMobile';

const RowSheet = ({ sheet, onClose }) => {
  // Escape closes it, and the body stops scrolling behind it — without
  // that, dragging the sheet scrolls the page underneath on iOS.
  useEffect(() => {
    if (!sheet) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [sheet, onClose]);

  if (!sheet) return null;

  const rows = (sheet.rows || []).filter(
    (r) => r && r.value !== undefined && r.value !== null && String(r.value).trim() !== '',
  );

  return (
    <>
      <div
        className="td-rowsheet-scrim"
        role="button"
        tabIndex={0}
        aria-label="Close details"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
      />

      <section className="td-rowsheet" role="dialog" aria-modal="true" aria-label={sheet.title}>
        <div className="td-rowsheet-grip" aria-hidden="true" />

        <header className="td-rowsheet-head">
          <div className="td-rowsheet-id">
            <h4 className="td-rowsheet-title">{sheet.title}</h4>
            {sheet.sub && <p className="td-rowsheet-sub">{sheet.sub}</p>}
          </div>
          <button type="button" className="td-rowsheet-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        {sheet.chips && sheet.chips.length > 0 && (
          <div className="td-rowsheet-chips">
            {sheet.chips.map((c) => (
              <span key={c.text} className={`td-chip${c.tone ? ` td-chip--${c.tone}` : ''}`}>
                {c.text}
              </span>
            ))}
          </div>
        )}

        <dl className="td-rowsheet-facts">
          {rows.map((r) => (
            <div className="td-rowsheet-fact" key={r.label}>
              <dt>{r.label}</dt>
              <dd className={r.tone ? `is-${r.tone}` : undefined}>{r.value}</dd>
            </div>
          ))}
        </dl>

        {sheet.actions && sheet.actions.length > 0 && (
          <footer className="td-rowsheet-actions">
            {sheet.actions.map((a, i) => (
              <button
                key={a.label}
                type="button"
                className={`td-card-btn${i === 0 ? ' td-card-btn--main' : ''}`}
                onClick={() => {
                  onClose();
                  a.onClick();
                }}
              >
                {a.label}
              </button>
            ))}
          </footer>
        )}
      </section>
    </>
  );
};

export default RowSheet;
